<template>
  <!-- [matrah] Template library: every Meta template grouped by name with per-language status. -->
  <div class="template-library">
    <div class="d-flex align-center flex-wrap mb-4" style="gap: 10px">
      <v-text-field v-model="search" density="compact" variant="outlined" hide-details prepend-inner-icon="mdi-magnify"
        placeholder="Search templates" style="max-width: 280px" clearable />
      <v-chip-group v-model="statusFilter" selected-class="text-primary" column>
        <v-chip v-for="s in statusOptions" :key="s" :value="s" size="small" filter variant="outlined" :color="statusColor(s)">
          {{ s }} <span class="ml-1 text-medium-emphasis">{{ templateStatusCounts[s] || 0 }}</span>
        </v-chip>
      </v-chip-group>
      <v-spacer />
      <v-btn variant="text" icon="mdi-refresh" title="Reload from Meta" :loading="isTemplatesLoading" @click="reload" />
      <v-btn color="primary" variant="flat" prepend-icon="mdi-plus" :to="{ name: 'templatesCreate' }">
        Create template
      </v-btn>
    </div>

    <v-alert v-if="templatesError" type="error" variant="tonal" density="compact" class="mb-3">
      Could not load templates: {{ templatesError }}
    </v-alert>

    <v-table class="library-table" fixed-header height="66vh">
      <thead>
        <tr>
          <th class="text-left">Template</th>
          <th class="text-left">Category</th>
          <th class="text-left">Languages &amp; status</th>
          <th class="text-right">Actions</th>
        </tr>
      </thead>
      <tbody v-if="filteredGroups.length">
        <tr v-for="group in filteredGroups" :key="group.name">
          <td>
            <div class="font-weight-medium template-name">{{ group.name }}</div>
            <div class="text-caption text-medium-emphasis">{{ group.languages.length }} language{{ group.languages.length === 1 ? '' : 's' }}</div>
          </td>
          <td>
            <v-chip size="small" variant="tonal" :color="group.category === 'MARKETING' ? 'secondary' : 'primary'">
              {{ group.category || '—' }}
            </v-chip>
          </td>
          <td>
            <div class="d-flex flex-wrap" style="gap: 6px">
              <v-chip v-for="lang in group.languages" :key="lang.key" size="small" variant="outlined"
                :color="statusColor(lang.status)" :title="lang.code + ' — ' + lang.status" class="lang-chip">
                <LanguageFlag :code="lang.code" :size="16" class="mr-1" />
                <span class="lang-code">{{ lang.code }}</span>
                <v-icon :icon="statusIcon(lang.status)" size="14" class="ml-1" />
                <span class="ml-1">{{ lang.status }}</span>
              </v-chip>
            </div>
          </td>
          <td class="text-right">
            <v-btn size="small" variant="text" color="error" prepend-icon="mdi-delete-outline" @click="askDelete(group)">
              Delete
            </v-btn>
          </td>
        </tr>
      </tbody>
      <tbody v-else>
        <tr>
          <td colspan="4" class="text-center text-medium-emphasis py-8">
            <template v-if="isTemplatesLoading">Loading templates from Meta…</template>
            <template v-else-if="templateGroups.length">No templates match the current filter.</template>
            <template v-else>No templates yet. Create the first one.</template>
          </td>
        </tr>
      </tbody>
    </v-table>

    <v-dialog v-model="deleteDialog" max-width="460">
      <v-card>
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon icon="mdi-alert" color="error" class="mr-2" /> Delete template
        </v-card-title>
        <v-card-text>
          <p class="text-body-1 mb-2">
            Delete <strong>{{ deleteTarget?.name }}</strong>?
          </p>
          <p class="text-body-2 text-medium-emphasis">
            All {{ deleteTarget?.languages.length }} language version(s) are removed from Meta. Messages already sent are
            unaffected, but the name cannot be reused for 30 days. This cannot be undone.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" :loading="deleting" @click="confirmDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useTemplateStore } from '@/stores/template'
import { useGlobalStore } from '@/stores/global'
import LanguageFlag from './LanguageFlag.vue'
import { statusColor } from '@/utils/templateLanguages'
// [matrah] toasts
import { toastSuccess, toastApiError } from '@/utils/notify'

export default {
  name: 'TemplateLibrary',
  components: { LanguageFlag },
  data: () => ({
    search: '',
    statusFilter: null,
    statusOptions: ['APPROVED', 'PENDING', 'REJECTED'],
    deleteDialog: false,
    deleteTarget: null,
    deleting: false,
  }),
  computed: {
    ...mapState(useTemplateStore, ['templateGroups', 'templateStatusCounts', 'isTemplatesLoading', 'templatesError']),
    filteredGroups() {
      const q = (this.search || '').trim().toLowerCase()
      return this.templateGroups.filter((g) => {
        if (q && !g.name.toLowerCase().includes(q)) return false
        if (this.statusFilter && !g.languages.some((l) => l.status === this.statusFilter)) return false
        return true
      })
    },
  },
  methods: {
    ...mapActions(useTemplateStore, ['getTemplates', 'deleteTemplate']),
    ...mapActions(useGlobalStore, ['setAlertMessage']),
    statusColor,
    statusIcon(status) {
      return {
        APPROVED: 'mdi-check-circle',
        PENDING: 'mdi-clock-outline',
        REJECTED: 'mdi-close-circle',
        PAUSED: 'mdi-pause-circle',
        DISABLED: 'mdi-cancel',
      }[status] || 'mdi-help-circle-outline'
    },
    async reload() {
      try {
        await this.getTemplates(true)
        // [matrah]
        toastSuccess('Templates refreshed from Meta.')
      } catch (e) {
        /* error is surfaced through templatesError */
        // [matrah] + toast
        toastApiError(e, 'Could not refresh templates')
      }
    },
    askDelete(group) {
      this.deleteTarget = group
      this.deleteDialog = true
    },
    async confirmDelete() {
      if (!this.deleteTarget) return
      this.deleting = true
      const res = await this.deleteTemplate(this.deleteTarget.name)
      this.deleting = false
      this.deleteDialog = false
      this.setAlertMessage({
        display: true,
        text: res.success ? `Template "${this.deleteTarget.name}" deleted.` : res.message || 'Delete failed.',
        class: res.success ? 'success' : 'error',
      })
      this.deleteTarget = null
    },
  },
}
</script>

<style scoped>
.template-name {
  font-family: 'Consolas', 'Courier New', monospace;
  font-size: 13px;
}

.lang-chip {
  text-transform: none;
}

.lang-code {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 11px;
}

.library-table :deep(th) {
  background: #f1f4f1 !important;
  font-weight: 600;
}
</style>
